/**
 * SB5-B.2 — External-IDs editor for the Contract Position Edit + Add
 * modals.
 *
 * Mirrors the pattern from
 * `apps/web/src/features/sites/shared/ExternalIdsField.tsx` — inline
 * repeating list of `{provider, id}` rows. We copy the pattern rather
 * than reach into Sites' internal `useFieldArray` form binding so
 * this modal can use react-hook-form OR plain state without the
 * Sites-specific `ExternalId` type signature.
 *
 * Inputs:
 *   - `value` — array of `{provider, id}` rows.
 *   - `onChange` — fires with the updated array on add/remove/edit.
 *   - `readOnly` — when true, inputs render readOnly and add/remove
 *     buttons hide.
 */
import { type ReactElement } from "react";
import {
  Box,
  Button,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { CircleMinusIcon, PlusIcon } from "@aeriosiq/ui/icons";
export interface ExternalIdRow {
  provider: string;
  id: string;
}

export interface ExternalIdsEditorProps {
  value: ExternalIdRow[];
  onChange: (next: ExternalIdRow[]) => void;
  readOnly?: boolean;
}

/**
 * Convert the BE's JSONB `externalIds` payload into the [{provider, id}]
 * shape this editor manages. Three shapes are accepted because the
 * gateway is historically inconsistent (Christian's SB6-G2 Bug-6
 * walkthrough surfaced the raw-array case):
 *
 *   1. `[{provider, id}, …]` raw array — the gateway's default for
 *      newly-created contract positions (`contract-positions-v1.service.ts`
 *      §createContractPosition emits `'[]'` when the request omits the
 *      key, and the column is stored / returned as-is).
 *   2. `{rows: [{provider, id}, …]}` envelope — older drafts.
 *   3. `{provider: "id"}` flat key/value map — the documented contract
 *      shape; what the editor folds back to via `rowsToObject`.
 *
 * Pre-fix the parser only handled (2) and (3); raw arrays fell into
 * `Object.entries` and produced one row with provider="0" and id =
 * `JSON.stringify(entry)` (Christian: "left value 0 and right the
 * entire jsonb dto").
 */
export function externalIdsObjectToRows(obj: unknown): ExternalIdRow[] {
  if (obj === null || obj === undefined) return [];
  // Shape 1 — raw `[{provider, id}, …]` array.
  if (Array.isArray(obj)) {
    return rowsFromArray(obj);
  }
  if (typeof obj !== "object") return [];
  // Shape 2 — `{rows: [{provider, id}, …]}` envelope.
  const rowsField = (obj as { rows?: unknown }).rows;
  if (Array.isArray(rowsField)) {
    return rowsFromArray(rowsField);
  }
  // Shape 3 — `{provider: "id"}` flat map (canonical FE→BE shape).
  return Object.entries(obj as Record<string, unknown>).map(
    ([provider, value]) => ({
      provider,
      id: typeof value === "string" ? value : JSON.stringify(value),
    }),
  );
}

function rowsFromArray(rows: unknown[]): ExternalIdRow[] {
  return rows
    .filter(
      (r): r is { provider?: unknown; id?: unknown } =>
        typeof r === "object" && r !== null,
    )
    .map((r) => ({
      provider: typeof r.provider === "string" ? r.provider : "",
      id: typeof r.id === "string" ? r.id : "",
    }));
}

/**
 * Reverse of `externalIdsObjectToRows` — fold the [{provider, id}]
 * rows back into a `{provider: id}` flat map for the PATCH body.
 * Empty rows are silently dropped.
 */
export function externalIdsRowsToObject(
  rows: ExternalIdRow[],
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const r of rows) {
    const p = r.provider.trim();
    const v = r.id.trim();
    if (p.length === 0 || v.length === 0) continue;
    out[p] = v;
  }
  return out;
}

export function ExternalIdsEditor({
  value,
  onChange,
  readOnly = false,
}: ExternalIdsEditorProps): ReactElement {
  function update(index: number, patch: Partial<ExternalIdRow>): void {
    const next = [...value];
    const current = next[index];
    if (!current) return;
    next[index] = { ...current, ...patch };
    onChange(next);
  }

  function remove(index: number): void {
    const next = value.filter((_, i) => i !== index);
    onChange(next);
  }

  function append(): void {
    onChange([...value, { provider: "", id: "" }]);
  }

  return (
    <Box>
      <Typography
        variant="overline"
        color="text.secondary"
        sx={{ display: "block", mb: 1 }}
      >
        External IDs
      </Typography>
      <Stack spacing={1}>
        {value.length === 0 ? (
          <Typography
            variant="body2"
            color="text.disabled"
            sx={{ fontStyle: "italic" }}
          >
            No external IDs.
          </Typography>
        ) : (
          value.map((row, index) => (
            <Stack key={index} direction="row" spacing={1} alignItems="center">
              <TextField
                value={row.provider}
                onChange={(e) => {
                  update(index, { provider: e.target.value });
                }}
                placeholder="Provider"
                size="small"
                sx={{ width: 200 }}
                slotProps={{
                  htmlInput: {
                    "aria-label": `External ID ${String(index + 1)} provider`,
                    ...(readOnly ? { readOnly: true } : {}),
                  },
                }}
              />
              <TextField
                value={row.id}
                onChange={(e) => {
                  update(index, { id: e.target.value });
                }}
                placeholder="ID"
                size="small"
                sx={{ flex: 1 }}
                slotProps={{
                  htmlInput: {
                    "aria-label": `External ID ${String(index + 1)} value`,
                    ...(readOnly ? { readOnly: true } : {}),
                  },
                }}
              />
              {!readOnly ? (
                <IconButton
                  aria-label={`Remove external ID ${String(index + 1)}`}
                  size="small"
                  onClick={() => {
                    remove(index);
                  }}
                >
                  <CircleMinusIcon fontSize="small" />
                </IconButton>
              ) : null}
            </Stack>
          ))
        )}
      </Stack>
      {!readOnly ? (
        <Button
          size="small"
          variant="text"
          startIcon={<PlusIcon fontSize="small" />}
          onClick={append}
          sx={{ mt: 1 }}
        >
          Add external ID
        </Button>
      ) : null}
    </Box>
  );
}

/**
 * Stable JSON-ish equality between two row arrays — used by the Edit
 * modal to detect "no changes to save" so the Save button can disable.
 */
export function externalIdsRowsEqual(
  a: ExternalIdRow[],
  b: ExternalIdRow[],
): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (a[i]?.provider !== b[i]?.provider || a[i]?.id !== b[i]?.id) {
      return false;
    }
  }
  return true;
}
